import {noop} from 'lodash';
import React from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {MessageTypesEnum} from '../../../../libs/shared/core/src/models/api/commonMessageModel';
import {DraftMessageModel} from '../../../../libs/shared/core/src/models/api/messageModel';
import {RecipientRolesEnum} from '../../../../libs/shared/core/src/models/api/recipientModel';
import {buildTeammateDisplayName} from '../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {ComposerPanelId, ViewerPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {
  InlineBanner,
  InlineBannerTypesEnum,
} from '../../../../libs/shared/ui/src/components/banners/inlineBanner/inlineBanner';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {AbsoluteDateTimeFormatterContainer} from '../../../../libs/shared/ui/src/components/datetime/absoluteDateTimeFormatterContainer';
import {Loader} from '../../../../libs/shared/ui/src/components/loaders/loader';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../../libs/shared/ui/src/styles/mixins';
import {FindTeammate} from '../../../store/data/teammates/teammatesSelectors';
import {RecipientRoleFormatter} from '../../common/formatters/recipientRoleFormatter';
import {MessageViewerEmailBody} from '../viewer/activity/message/email/messageViewerEmailBody';
import {MessageViewerAttachmentsContainer} from '../viewer/activity/message/messageViewerAttachmentsContainer';

/*
 * Props.
 */

interface ComposerResolveConflictsModalContentProps {
  panelId: ComposerPanelId | ViewerPanelId;
  message?: DraftMessageModel;
  findTeammate: FindTeammate;
  hasError: boolean;
  onResolveLatest: () => void;
  onDiscardLatest: (arg: string | undefined) => void;
}

/*
 * Styles.
 */

const horizontalPadding = 16;

const StyledDetailWrap = styled.div`
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  padding: 6px ${horizontalPadding}px;
`;

const StyledDetailRow = styled.div`
  padding: 4px 0;
`;

const StyledDetailLabel = styled.span`
  color: ${(p) => p.theme.greys.shade70};
  padding-right: 6px;
`;

const StyledCtaWrap = styled.div`
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  padding: 12px ${horizontalPadding}px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const StyledAuthor = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  margin-right: auto;
`;

const StyledLoaderWrap = styled.div`
  height: 200px;
  ${center()}
`;

const StyledInfoBanner = styled(InlineBanner)`
  margin: ${horizontalPadding}px;
`;

/*
 * Component.
 */

export const ComposerResolveConflictsModalContent = ({
  message,
  findTeammate,
  onResolveLatest,
  onDiscardLatest,
  hasError,
  panelId,
}: ComposerResolveConflictsModalContentProps) => {
  if (hasError) {
    return (
      <StyledInfoBanner
        type={InlineBannerTypesEnum.ERROR}
        title={
          <Trans id="composer-resolve-conflicts-cant-load-error">
            We can&apos;t load latest message right now. Please try again.
          </Trans>
        }
      />
    );
  }

  if (!message) {
    return (
      <StyledLoaderWrap>
        <Loader />
      </StyledLoaderWrap>
    );
  }

  const isEmail = message.type === MessageTypesEnum.EMAIL;

  return (
    <>
      {maybeRenderRecipients(message)}
      {isEmail ? (
        <StyledDetailWrap>
          <StyledDetailRow>
            <StyledDetailLabel>
              <Trans id="composer-resolve-conflicts-subject">Subject:</Trans>
            </StyledDetailLabel>
            {message.subject}
          </StyledDetailRow>
        </StyledDetailWrap>
      ) : null}
      <MessageViewerAttachmentsContainer
        message={message}
        timelineVariation={TimelineVariationsEnum.ASYNCHRONOUS}
        shouldScroll
      />
      <MessageViewerEmailBody
        message={message}
        isExternalContentHidden
        hasMessageAnnotations={false}
        panelId={panelId}
        onOpenMessageRenderClick={noop}
      />
      <StyledCtaWrap>
        {maybeRenderLastAuthor(message, findTeammate)}
        <ButtonGroup>
          <ButtonGroupSpacer />
          <Button
            variant={ButtonVariantsEnum.SECONDARY}
            size={VisualSizesEnum.MEDIUM}
            onClick={() => onDiscardLatest(message.version)}
          >
            <Trans id="composer-resolve-conflicts-discard-latest">Discard this version</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            size={VisualSizesEnum.MEDIUM}
            onClick={onResolveLatest}
          >
            <Trans id="composer-resolve-conflicts-use-latest">Use this version</Trans>
          </Button>
        </ButtonGroup>
      </StyledCtaWrap>
    </>
  );
};

/*
  Helpers.
*/

function maybeRenderRecipients(message: DraftMessageModel) {
  const isEmail = message.type === MessageTypesEnum.EMAIL;
  const isChat = message.type === MessageTypesEnum.FRONT_CHAT;
  const isTwitter = message.type === MessageTypesEnum.TWEET || message.type === MessageTypesEnum.TWEET_DM;
  const shouldShowToRecipient = isEmail || isChat || isTwitter;

  if (!isEmail && !shouldShowToRecipient) {
    return null;
  }

  return (
    <StyledDetailWrap>
      {isEmail ? maybeRenderRecipient(message, RecipientRolesEnum.FROM) : null}
      {shouldShowToRecipient ? maybeRenderRecipient(message, RecipientRolesEnum.TO) : null}
      {isEmail ? maybeRenderRecipient(message, RecipientRolesEnum.CC) : null}
      {isEmail ? maybeRenderRecipient(message, RecipientRolesEnum.BCC) : null}
    </StyledDetailWrap>
  );
}

function maybeRenderRecipient(message: DraftMessageModel, role: RecipientRolesEnum) {
  const filteredRecipients = message.recipients.filter((r) => r.role === role);
  if (!filteredRecipients.length) {
    return null;
  }

  return (
    <StyledDetailRow>
      <StyledDetailLabel>
        <RecipientRoleFormatter role={role} />:
      </StyledDetailLabel>
      {filteredRecipients.map((r) => r.handle).join(', ')}
    </StyledDetailRow>
  );
}

function maybeRenderLastAuthor(message: DraftMessageModel, findTeammate: FindTeammate) {
  const {lastNonVisitorAuthorId, updatedAt} = message;
  const author = findTeammate(lastNonVisitorAuthorId);
  if (!author) {
    return null;
  }

  return (
    <StyledAuthor>
      <Trans id="composer-resolve-conflicts-last-updated-by">
        Last updated by {buildTeammateDisplayName(author)} on
      </Trans>{' '}
      <AbsoluteDateTimeFormatterContainer value={updatedAt} />
    </StyledAuthor>
  );
}
